import { Spacer } from "@hackthenorth/north";
import { Label } from "north.js";
import React from "react";

import { SponsorCompanyTier } from "src/api/types.generated";
import { Dropzone, FileLink } from "src/shared/components";
import { useSponsorContext } from "src/shared/contexts";
import { answersToNorthV2Options } from "src/shared/utils/react-select";
import {
  PerkStatus,
  SponsorPerkType,
  SponsorPerksData,
} from "src/views/sponsor/perks/types";

import {
  PerkContainer,
  PerkError,
  PerkSelect,
  PerkTextArea,
} from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription, TextInputTitle } from "../TextComponents";
import { useFileDropzone } from "../useFileDropzone";
import { PerkData } from "../usePerkState";

import {
  DEFAULT_PROMOTION_TYPE_OPTIONS,
  PROMOTION_TYPE_OPTIONS_BY_TIER,
} from "./constants";

export const SOCIAL_MEDIA_PROMOTION_VALIDATION_FIELDS: (keyof SponsorPerksData)[] =
  ["social_media_promotion_type", "social_media_promotion_description"];

export const SocialMediaPromotion: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const {
    dropzoneInputRef,
    fetchedFile,
    error: fileError,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(getState("social_media_promotion_logo") ?? [], (files) =>
    updateState("social_media_promotion_logo", files)
  );

  const { company } = useSponsorContext();
  const promotionTypeOptions = company?.tier
    ? PROMOTION_TYPE_OPTIONS_BY_TIER[company.tier as SponsorCompanyTier] ??
      DEFAULT_PROMOTION_TYPE_OPTIONS
    : DEFAULT_PROMOTION_TYPE_OPTIONS;

  return (
    <>
      <Header
        title="Social Media Promotion"
        subtitle="This perk is for promoting your company on our social media."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("social_media_promotion_status") as
                | PerkStatus
                | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("social_media_promotion_status") as
                | PerkStatus
                | undefined
            )}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.SOCIAL_MEDIA_PROMOTION}
        readOnly={isReadOnly}
      >
        <PerkSelect
          fullWidth
          size="lg"
          value={getState("social_media_promotion_type") ?? ""}
          onChange={(e) =>
            updateState("social_media_promotion_type", e.target.value)
          }
          label="Promotion Type*"
          placeholder="Select promotion type"
          isReadOnly={isReadOnly}
          error={
            error?.social_media_promotion_type && <>This field is required</>
          }
        >
          {answersToNorthV2Options(promotionTypeOptions)}
        </PerkSelect>
        <Spacer height={32} />
        <TextInputTitle>
          Logo / Graphic{" "}
          <Label color="secondary-light" size="sm">
            Optional
          </Label>
        </TextInputTitle>
        <Spacer height={8} />
        {!isReadOnly && (
          <Dropzone
            max={1}
            acceptedFileTypes={["png", "svg"]}
            onFilesUpload={addFiles}
            onError={onFileError}
            ref={dropzoneInputRef}
            onBlur={() => setDropzoneErrorMessage("")}
          />
        )}
        {fetchedFile && (
          <>
            <Spacer height={32} />
            <FileLink file={fetchedFile} deleteFile={deleteFile} />
          </>
        )}
        {fileError && <PerkError error={fileError} />}
        <Spacer height={32} />
        <PerkTextArea
          title="Description* (Max 150 Words)"
          value={getState("social_media_promotion_description") ?? ""}
          placeholder="Ex. Summer internships are now open! Head to mycompany.com/careers and apply before August 1st!"
          isReadOnly={isReadOnly}
          wordLimit={150}
          onChange={(e) =>
            updateState("social_media_promotion_description", e.target.value)
          }
          error={
            error?.social_media_promotion_description && (
              <>This field is required</>
            )
          }
        />
      </PerkContainer>
    </>
  );
};

const Description = () => (
  <TextDescription>
    If you would like something promoted on our social media, please provide an
    optional logo/graphic and a description to be posted on our Instagram,
    LinkedIn, or other media outlets. Please note that our team will use these
    materials to create a post, and your Sponsorship Coordinator will send it to
    you for review before it is published.
  </TextDescription>
);
